import { useMemo, useState, useEffect } from "react"
import { Modal, Text } from "@mantine/core"
import { useModals } from "@mantine/modals"
import { useNotifications } from "@mantine/notifications"
import { SelectColumnFilter } from "components/ui/table/TableHeader"
import {
  DateCell,
  EditActions,
  AvatarCell,
  GetQrCode,
  ToggleActiveCell,
} from "components/ui/table/TableUtils"
import { Icon } from "@material-tailwind/react"
import { getAll, deleteByID, update, createQR } from "services/api.service"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"
import Table from "components/ui/table/Table"
import EmployeeForm from "components/ui/forms/EmployeeForm"

const Employees = () => {
  const emptyEmployee = {
    first_name: "",
    last_name_1: "",
    last_name_2: "",
    start_date: "",
    end_date: "",
    worksites: [],
    work: "",
    comment: "",
    file: "",
  }

  const API_ENDPOINT = "/employees"

  const [employees, setEmployees] = useState([])
  const [isCreation, setIsCreation] = useState(false)
  const [refresh, setRefresh] = useState(false)
  const [editEmployeeData, setEditEmployeeData] = useState(emptyEmployee)
  const [opened, setOpened] = useState(false)

  const modals = useModals()
  const notifications = useNotifications()

  const { loading, callEndpoint } = useFetchAndLoad()

  const setData = async () => {
    await callEndpoint(getAll(API_ENDPOINT))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setEmployees(response.data.data)
        }
      })
      .catch((error) => {
        notifications.showNotification({
          color: "red",
          title: "Error",
          message: "Error al obtener a los empleados",
        })
      })
  }

  useEffect(() => {
    setData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh])

  const onEdit = (employee) => {
    setEditEmployeeData(employee.original)
    setIsCreation(false)
    setOpened(true)
  }

  const onDelete = (employee) => {
    openConfirmDeleteModal(employee)
  }

  const openConfirmDeleteModal = (employee) => {
    modals.openConfirmModal({
      title: "Por favor confirma la eliminación",
      children: (
        <Text size='sm'>
          ¿Seguro de que deseas eliminar al empleado
          <span className='font-bold'>
            {" " + employee.original.first_name} {employee.original.last_name_1}{" "}
            {employee.original.last_name_2}
          </span>
          ? <br />
          Esta acción no se puede deshacer.
        </Text>
      ),
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: {
        className: "bg-red-600 text-white hover:bg-red-500",
      },
      onCancel: () => {},
      onConfirm: async () => {
        notifications.showNotification({
          color: "blue",
          title: "Eliminando",
          loading: true,
          message: "Eliminando empleado.",
        })
        await callEndpoint(deleteByID(API_ENDPOINT, employee.original._id))
          .then((response) => {
            if (response.status === 200 || response.status === 201) {
              setData()
              notifications.clean()
              notifications.showNotification({
                color: "green",
                title: "Empleado eliminado",
                message: "Empleado ha sido eliminado con éxito.",
                icon: <Icon name={"check"} />,
              })
            }
          })
          .catch((error) => {
            notifications.clean()
            notifications.showNotification({
              color: "red",
              title: "Error",
              message: "Error al eliminar empleado.",
              icon: <Icon name={"close"} />,
            })
          })
      },
    })
  }

  const displayQrCode = async (employee, windowReference) => {
    const getQrEndpoint = `/access/code`
    employee = employee.original

    const employeeForQrResquest = {
      id: employee._id,
      type: "employee",
    }

    await callEndpoint(createQR(getQrEndpoint, employeeForQrResquest))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const file = new Blob([response.data], { type: "application/pdf" })
          const fileURL = URL.createObjectURL(file)
          windowReference.location = fileURL

          notifications.showNotification({
            color: "green",
            title: "Acceso creado",
            message: "El acceso ha sido creado con éxito.",
            icon: <Icon name={"check"} />,
          })
        }
      })
      .catch((error) => {
        notifications.clean()
        notifications.showNotification({
          color: "red",
          title: "Error",
          message: "Error al generar el codigo de acceso.",
          icon: <Icon name={"close"} />,
        })
      })
  }

  const toggleActive = async (employee) => {
    const employeeIdAndStatus = {
      _id: employee.original._id,
      active: !employee.original.active,
    }

    const prevSatatus = employee.original.active

    await callEndpoint(update(`${API_ENDPOINT}`, employeeIdAndStatus))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setData()
          notifications.clean()
          notifications.showNotification({
            color: "green",
            title: `Acceso ${prevSatatus ? "desactivado" : "activado"}`,
            message: `Acceso ${prevSatatus ? "desactivado" : "activado"} para el empleado ${
              employee.original.first_name
            } ${employee.original.last_name_1} ${employee.original.last_name_2}.`,
            icon: <Icon name={"check"} />,
          })
        }
      })
      .catch((error) => {
        notifications.clean()
        notifications.showNotification({
          color: "red",
          title: "Error",
          message: `Error al intentar habilitar el acceso para ${employee.original.first_name} ${employee.original.last_name_1} ${employee.original.last_name_2}.`,
          icon: <Icon name={"close"} />,
        })
      })
  }

  const columns = useMemo(
    () => [
      {
        Header: "Nombre",
        accessor: "first_name",
        Cell: AvatarCell,
        imgAccesor: "image",
      },
      {
        Header: "Apellido Paterno",
        accessor: "last_name_1",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Apellido Materno",
        accessor: "last_name_2",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Fecha Inicio",
        accessor: "start_date",
        Cell: DateCell,
      },
      {
        Header: "Fecha Fin",
        accessor: "end_date",
        Cell: DateCell,
      },
      {
        Header: "Activo",
        accessor: "active",
        Cell: ({ row }) => <ToggleActiveCell value={row} onChange={toggleActive} />,
      },
      {
        Header: "Acciones",
        Cell: ({ row }) => (
          <div className='flex flex-basis flex-wrap justify-end lg:justify-start'>
            <GetQrCode
              value={row}
              onClick={() => {
                var windowReference = window.open()
                displayQrCode(row, windowReference)
              }}
            />
            <EditActions value={row} onEdit={onEdit} onDelete={onDelete} />
          </div>
        ),
      },
    ],
    []
  )

  return (
    <>
      <Table
        columns={columns}
        data={employees}
        iconName={"person"}
        editable={true}
        setOpened={setOpened}
        setIsCreation={setIsCreation}
        loading={loading}
        header={"Empleados"}
      />

      <Modal
        opened={opened}
        onClose={() => {
          setEditEmployeeData(emptyEmployee)
          setOpened(false)
        }}
        title={isCreation ? "Crear Empleado" : "Editar Empleado"}
        size='xl'>
        <EmployeeForm
          data={editEmployeeData}
          refresh={() => {
            setRefresh(!refresh)
            setOpened(false)
            setEditEmployeeData(emptyEmployee)
          }}
          isCreation={isCreation}
          notifications={notifications}
          endpoint={API_ENDPOINT}
        />
      </Modal>
    </>
  )
}

export default Employees
